import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import {
    MarkButton,
    MarkChoice,
    MarkChoiceLabel,
    MarkInput,
    MarkSelect,
    MarkTextArea,
    MarkLabel
} from '../MarkControls';
import type {
    Block,
    HeadingBlock,
    ParagraphBlock,
    ButtonBlock,
    ImageBlock,
    ListBlock,
    SpacerBlock,
    TwoColumnBlock,
    CardBlock,
    TestimonialBlock,
    ConditionalBlock,
    ConditionalOperator
} from '../../types';
import { useEditorContext } from './EditorContext';
import { useInsertTarget } from './useInsertTarget';
import EditableBlockList from './EditableBlockList';
import PersonalizationButton from './PersonalizationButton';
import { contentFieldErrors } from './contentChoices';
import * as layout from '../markLayout.module.css';
import * as styles from './canvas.module.css';

type Props = { block: Block; onChange: (updated: Block) => void; depth?: number };
type EditProps<T extends Block> = { block: T; onChange: (updated: Block) => void };
const TextField: React.FC<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    multiline?: boolean;
    link?: boolean;
    error?: string;
    help?: string;
    personalization?: boolean | 'links';
}> = ({ label, value, onChange, multiline, link, error, help, personalization = true }) => {
    const id = useId();
    const { registerField, clearInsertTarget } = useInsertTarget();
    const props = {
        id,
        value,
        'aria-invalid': !!error,
        'aria-describedby': error ? `${id}-error` : help ? `${id}-help` : undefined,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onChange(event.currentTarget.value),
        onFocus: personalization
            ? registerField(id, onChange, personalization === 'links' ? 'links' : undefined)
            : clearInsertTarget,
        onBlur: personalization
            ? registerField(id, onChange, personalization === 'links' ? 'links' : undefined)
            : clearInsertTarget
    };
    return (
        <div className={`${styles.field} ${multiline ? styles.message : ''}`}>
            <div className={styles.fieldLabel}>
                <MarkLabel htmlFor={id}>{label}</MarkLabel>
                {personalization && (
                    <PersonalizationButton
                        fieldId={id}
                        onInsert={onChange}
                        mode={personalization === 'links' ? 'links' : 'text'}
                    />
                )}
            </div>
            {multiline ? (
                <MarkTextArea {...props} rows={5} placeholder="Write your message…" />
            ) : (
                <MarkInput
                    {...props}
                    type="text"
                    inputMode={link ? 'url' : undefined}
                    spellCheck={link ? false : undefined}
                    autoComplete="off"
                    placeholder={link ? 'https://example.com/…' : undefined}
                />
            )}
            {error && (
                <P id={`${id}-error`} noDefaultMargin size="small" className={styles.error}>
                    {error}
                </P>
            )}
            {help && (
                <P
                    id={`${id}-help`}
                    noDefaultMargin
                    size="small"
                    styles={{ color: 'var(--mark-muted)' }}
                >
                    {help}
                </P>
            )}
        </div>
    );
};
const useErrors = (block: Block) => {
    const { showErrors, validation } = useEditorContext();
    return showErrors ? contentFieldErrors(block, validation) : {};
};
const EditableBlock: React.FC<Props> = ({ block, onChange, depth = 0 }) => {
    switch (block.type) {
        case 'heading':
            return <HeadingEditor block={block} onChange={onChange} />;
        case 'paragraph':
            return <ParagraphEditor block={block} onChange={onChange} />;
        case 'button':
            return <ButtonEditor block={block} onChange={onChange} />;
        case 'image':
            return <ImageEditor block={block} onChange={onChange} />;
        case 'list':
            return <ListEditor block={block} onChange={onChange} />;
        case 'divider':
            return (
                <P noDefaultMargin size="small">
                    A horizontal line separates the sections above and below.
                </P>
            );
        case 'spacer':
            return <SpacerEditor block={block} onChange={onChange} />;
        case 'two_column':
            return <TwoColumnEditor block={block} onChange={onChange} depth={depth} />;
        case 'card':
            return <CardEditor block={block} onChange={onChange} depth={depth} />;
        case 'testimonial':
            return <TestimonialEditor block={block} onChange={onChange} />;
        case 'conditional':
            return <ConditionalEditor block={block} onChange={onChange} depth={depth} />;
    }
};
const HeadingEditor: React.FC<EditProps<HeadingBlock>> = ({ block, onChange }) => {
    const sizeId = useId();
    const { clearInsertTarget } = useInsertTarget();
    const errors = useErrors(block);
    const sizes = [
        'Main heading',
        'Section heading',
        'Subheading',
        'Small heading',
        'Smaller heading',
        'Smallest heading'
    ];
    return (
        <div className={layout.stack}>
            <TextField
                label="Heading text"
                value={block.text}
                onChange={(text) => onChange({ ...block, text })}
                error={errors.text}
            />
            <div className={styles.field} style={{ maxWidth: '28rem' }}>
                <MarkLabel htmlFor={sizeId}>Heading size</MarkLabel>
                <MarkSelect
                    id={sizeId}
                    value={block.level ?? 2}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({
                            ...block,
                            level: Number(event.currentTarget.value) as HeadingBlock['level']
                        })
                    }
                >
                    {sizes.map((size, index) => (
                        <option key={size} value={index + 1}>
                            {size}
                        </option>
                    ))}
                </MarkSelect>
            </div>
        </div>
    );
};
const ParagraphEditor: React.FC<EditProps<ParagraphBlock>> = ({ block, onChange }) => {
    const errors = useErrors(block);
    // Appearance comes from the template. Preserve any existing style while editing text.
    return (
        <TextField
            label="Message"
            multiline
            value={block.text}
            onChange={(text) => onChange({ ...block, text })}
            error={errors.text}
        />
    );
};
const ButtonEditor: React.FC<EditProps<ButtonBlock>> = ({ block, onChange }) => {
    const errors = useErrors(block);
    return (
        <div className={layout.stack}>
            <TextField
                label="Button label"
                value={block.text}
                onChange={(text) => onChange({ ...block, text })}
                error={errors.text}
            />
            <TextField
                label="Destination link"
                personalization="links"
                link
                value={block.url}
                onChange={(url) => onChange({ ...block, url })}
                error={errors.url}
                help="Enter a website address or insert a saved link."
            />
        </div>
    );
};
const ImageEditor: React.FC<EditProps<ImageBlock>> = ({ block, onChange }) => {
    const errors = useErrors(block);
    return (
        <div className={layout.stack}>
            <TextField
                label="Image link"
                personalization={false}
                link
                value={block.src}
                onChange={(src) => onChange({ ...block, src })}
                error={errors.src}
                help="Use the link to an image that is already hosted online."
            />
            <TextField
                label="Image description"
                value={block.alt ?? ''}
                onChange={(alt) => onChange({ ...block, alt: alt || undefined })}
                help="Describe the image for readers using a screen reader. Leave blank for a decorative image."
            />
        </div>
    );
};
const ListEditor: React.FC<EditProps<ListBlock>> = ({ block, onChange }) => {
    const styleId = useId();
    const { clearInsertTarget } = useInsertTarget();
    const errors = useErrors(block);
    return (
        <div className={layout.stack}>
            <div className={styles.field}>
                <MarkLabel htmlFor={styleId}>List style</MarkLabel>
                <MarkSelect
                    id={styleId}
                    value={block.ordered ? 'numbered' : 'bulleted'}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({ ...block, ordered: event.currentTarget.value === 'numbered' })
                    }
                >
                    <option value="bulleted">Bullet points</option>
                    <option value="numbered">Numbered steps</option>
                </MarkSelect>
            </div>
            {block.items.map((item, index) => (
                <div key={index} className={layout.stack}>
                    <TextField
                        label={`List item ${index + 1}`}
                        value={item}
                        onChange={(value) =>
                            onChange({
                                ...block,
                                items: block.items.map((previous, i) =>
                                    i === index ? value : previous
                                )
                            })
                        }
                        error={errors[`item-${index}`]}
                    />
                    <div className={styles.actions}>
                        <MarkButton
                            aria-label={`Remove list item ${index + 1}`}
                            onClick={() => {
                                clearInsertTarget();
                                onChange({
                                    ...block,
                                    items: block.items.filter((_, i) => i !== index)
                                });
                            }}
                        >
                            Remove item
                        </MarkButton>
                    </div>
                </div>
            ))}
            <div className={styles.actions}>
                <MarkButton
                    onClick={() => {
                        clearInsertTarget();
                        onChange({ ...block, items: [...block.items, ''] });
                    }}
                >
                    Add list item
                </MarkButton>
            </div>
        </div>
    );
};
const SpacerEditor: React.FC<EditProps<SpacerBlock>> = ({ block, onChange }) => {
    const id = useId();
    const { clearInsertTarget } = useInsertTarget();
    return (
        <div className={styles.field} style={{ maxWidth: '28rem' }}>
            <MarkLabel htmlFor={id}>Space between sections (pixels)</MarkLabel>
            <MarkInput
                id={id}
                type="number"
                min={0}
                max={200}
                step={1}
                value={block.height ?? ''}
                placeholder="16"
                onFocus={clearInsertTarget}
                onChange={(event) => {
                    const height = event.currentTarget.valueAsNumber;
                    onChange({
                        ...block,
                        height: Number.isNaN(height)
                            ? undefined
                            : Math.min(200, Math.max(0, Math.trunc(height)))
                    });
                }}
            />
        </div>
    );
};
const TwoColumnEditor: React.FC<EditProps<TwoColumnBlock> & { depth: number }> = ({
    block,
    onChange,
    depth
}) => {
    const { channel } = useEditorContext();
    return (
        <div className={layout.stack}>
            <EditableBlockList
                blocks={block.left}
                onChange={(left) => onChange({ ...block, left })}
                channel={channel}
                depth={depth + 1}
                label="Left column"
            />
            <EditableBlockList
                blocks={block.right}
                onChange={(right) => onChange({ ...block, right })}
                channel={channel}
                depth={depth + 1}
                label="Right column"
            />
        </div>
    );
};
const CardEditor: React.FC<EditProps<CardBlock> & { depth: number }> = ({
    block,
    onChange,
    depth
}) => {
    const id = useId();
    const { channel } = useEditorContext();
    const { clearInsertTarget } = useInsertTarget();
    return (
        <div className={layout.stack}>
            <div className={styles.field}>
                <MarkLabel htmlFor={id}>Card appearance</MarkLabel>
                <MarkSelect
                    id={id}
                    value={block.style ?? 'elevated'}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({
                            ...block,
                            style: event.currentTarget.value as CardBlock['style']
                        })
                    }
                >
                    <option value="elevated">Raised card</option>
                    <option value="flat">Outlined card</option>
                </MarkSelect>
            </div>
            <EditableBlockList
                blocks={block.children}
                onChange={(children) => onChange({ ...block, children })}
                channel={channel}
                depth={depth + 1}
                label="Card content"
            />
        </div>
    );
};
const TestimonialEditor: React.FC<EditProps<TestimonialBlock>> = ({ block, onChange }) => {
    const starsId = useId(),
        verifiedId = useId();
    const { clearInsertTarget } = useInsertTarget();
    const errors = useErrors(block);
    return (
        <div className={layout.stack}>
            <TextField
                label="Review quote"
                multiline
                value={block.quote}
                onChange={(quote) => onChange({ ...block, quote })}
                error={errors.quote}
            />
            <TextField
                label="Reviewer name (optional)"
                value={block.source ?? ''}
                onChange={(source) => onChange({ ...block, source: source || undefined })}
            />
            <TextField
                label="Reviewer details (optional)"
                value={block.byline ?? ''}
                onChange={(byline) => onChange({ ...block, byline: byline || undefined })}
            />
            <div className={styles.field}>
                <MarkLabel htmlFor={starsId}>Rating out of 5 (optional)</MarkLabel>
                <MarkInput
                    id={starsId}
                    type="number"
                    min={0}
                    max={5}
                    step="any"
                    value={block.stars ?? ''}
                    onFocus={clearInsertTarget}
                    onChange={(event) => {
                        const stars = event.currentTarget.valueAsNumber;
                        onChange({
                            ...block,
                            stars: Number.isNaN(stars) ? undefined : Math.min(5, Math.max(0, stars))
                        });
                    }}
                />
            </div>
            <MarkChoiceLabel htmlFor={verifiedId}>
                <MarkChoice
                    id={verifiedId}
                    type="checkbox"
                    checked={block.verified ?? false}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({ ...block, verified: event.currentTarget.checked })
                    }
                />
                Verified review
            </MarkChoiceLabel>
        </div>
    );
};
const ConditionalEditor: React.FC<EditProps<ConditionalBlock> & { depth: number }> = ({
    block,
    onChange,
    depth
}) => {
    const variableId = useId(),
        operatorId = useId();
    const { channel, variables } = useEditorContext();
    const { clearInsertTarget } = useInsertTarget();
    const errors = useErrors(block);
    return (
        <div className={layout.stack}>
            <div className={styles.field}>
                <MarkLabel htmlFor={variableId}>Recipient detail</MarkLabel>
                <MarkSelect
                    id={variableId}
                    value={block.variable}
                    aria-invalid={!!errors.variable}
                    aria-describedby={errors.variable ? `${variableId}-error` : undefined}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({ ...block, variable: event.currentTarget.value })
                    }
                >
                    <option value="">Choose a recipient detail…</option>
                    {block.variable &&
                        !variables.some((variable) => variable.key === block.variable) && (
                            <option value={block.variable}>{block.variable} (unavailable)</option>
                        )}
                    {variables.map((variable) => (
                        <option key={variable.key} value={variable.key}>
                            {variable.label}
                        </option>
                    ))}
                </MarkSelect>
                {errors.variable && (
                    <P
                        id={`${variableId}-error`}
                        noDefaultMargin
                        size="small"
                        className={styles.error}
                    >
                        {errors.variable}
                    </P>
                )}
            </div>
            <div className={styles.field}>
                <MarkLabel htmlFor={operatorId}>Show content when this detail</MarkLabel>
                <MarkSelect
                    id={operatorId}
                    value={block.operator}
                    onFocus={clearInsertTarget}
                    onChange={(event) =>
                        onChange({
                            ...block,
                            operator: event.currentTarget.value as ConditionalOperator
                        })
                    }
                >
                    <option value="eq">Equals</option>
                    <option value="neq">Does not equal</option>
                    <option value="exists">Is provided</option>
                </MarkSelect>
            </div>
            {block.operator !== 'exists' && (
                <TextField
                    label="Value to match"
                    personalization={false}
                    value={block.value ?? ''}
                    onChange={(value) => onChange({ ...block, value })}
                />
            )}
            <EditableBlockList
                blocks={block.children}
                onChange={(children) => onChange({ ...block, children })}
                channel={channel}
                depth={depth + 1}
                label="When the condition matches"
            />
            <EditableBlockList
                blocks={block.else_children ?? []}
                onChange={(else_children) =>
                    onChange({
                        ...block,
                        else_children: else_children.length ? else_children : undefined
                    })
                }
                channel={channel}
                depth={depth + 1}
                label="Otherwise"
            />
        </div>
    );
};
export default EditableBlock;
